import { useCallback, useMemo, useState } from 'react';
import { Dispatch } from 'redux';

// eslint-disable-next-line import/no-restricted-paths
import { TApplicationContext } from 'node/types';

import { useReduxDispatch } from 'shared/hooks/useReduxDispatch';
import { AnyFunction } from 'shared/types/AnyFunction';
import { IApplicationState } from 'shared/types/redux';

import { FetchModel } from './internal/models/FetchModel';
import { CallRequest, UnknownResponse } from './types';

/**
 * @description Хук предоставляет возможность накапливать состояния запросов компонента, а именно,
 * данные ответа от сервера, об ошибке и состоянии выполнения запроса.
 * Так же хук предоставляет возможность использования коллбеков на каждой стадии выполнения запроса
 * @example
 * const { callRequest, data, pending, error } = useFetch<
 *  Response200, Response400, Omit<Options<Response200, Response400>, 'httpApi'>
 * >(fetchBookCoworking, 'Сообщение об ошибке в логгер');
 *
 * callRequest(
 *   { parameters: {} },
 *   {
 *     onRequest: () => null,
 *     onSuccess: (response: Response200) => null,
 *     onFailure: (response: Response400) => null,
 *     onCriticalError: (error: Error, meta: ErrorMeta) => null,
 *     onDone: () => null,
 *   },
 * )
 * @param {FetchFunction} fetchFunction функция, которая будет вызвана для осуществления запроса на сервер
 * @param {string} errorComment комментарий для логгера
 * @param {boolean} initialPending начальное состояние запроса
 * @param {Response200 | null} initialData начальные данные успешно выполненного запроса
 * @param {Response400 | null} initialError начальные данные ошибочного запроса
 * @returns {{data: Response200 | null, pending: boolean, callRequest: <Params, Response200, Response400>(parameters:
 *   Params, callback?: {onRequest?: AnyFunction, onSuccess?: AnyFunction<[Response200]>, onFailure?:
 *   AnyFunction<[Response400]>}) => void, error: Response400 | null}}
 */
export function useFetch<
  Response200 extends UnknownResponse,
  Response400 extends UnknownResponse,
  Params,
  FetchFunction extends AnyFunction = AnyFunction,
>(
  fetchFunction: FetchFunction,
  errorComment: string,
  initialPending: boolean = false,
  initialData: Response200 | null = null,
  initialError: Response400 | null = null,
) {
  const [dataState, setDataState] = useState<Response200 | null>(initialData);
  const [errorState, setErrorState] = useState<Response400 | null>(initialError);
  const [pendingState, setPendingState] = useState<boolean>(initialPending);

  const dispatch = useReduxDispatch();

  const callRequest = useCallback<CallRequest<Params, Response200, Response400>>(
    async (parameters, callback = Object.create(null)) => {
      setPendingState(true);

      dispatch(async (_dispatch: Dispatch, _getState: () => IApplicationState, context: TApplicationContext) => {
        const { httpApi, logger } = context;
        const { onFailure, onSuccess, onRequest, onCriticalError, onDone } = callback;

        onRequest?.();

        try {
          const response: Response200 | Response400 = await fetchFunction({ httpApi, ...parameters });

          switch (response.statusCode) {
            case 200: {
              const response200 = response as Response200;
              setDataState(response200);

              if (errorState !== null) {
                setErrorState(null);
              }

              onSuccess?.(response200);

              break;
            }

            case 400: {
              const response400 = response as Response400;

              setErrorState(response400);

              onFailure?.(response400);

              break;
            }

            default: {
              throw new Error(`Статус ${response.statusCode} не поддерживается`);
            }
          }
        } catch (error) {
          const loggerErrorMeta = {
            comment: errorComment,
          };

          onCriticalError?.(error, loggerErrorMeta);

          logger.error(error, loggerErrorMeta);
        } finally {
          setPendingState(false);

          onDone?.();
        }
      });
    },
    [dispatch, errorComment, errorState, fetchFunction],
  );

  return useMemo(
    () => new FetchModel(callRequest, dataState, errorState, pendingState),
    [callRequest, dataState, errorState, pendingState],
  );
}
